"use client";

import React from "react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Mail, Inbox, Link2, Tag } from "lucide-react";
import {
  getCommunicationEmailSourceBadgeClass,
  parseCommunicationEmailProvenance,
} from "@/lib/utils/crm/communication-email-provenance";

interface CommunicationEmailProvenanceCardProps {
  metadata: unknown;
  title?: string;
  compact?: boolean;
}

function ProvenanceField({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div>
      <span className="text-gray-600 dark:text-gray-400">{label}</span>
      <p
        className={`text-gray-900 dark:text-gray-100 font-medium mt-1 break-all ${
          mono ? "font-mono text-xs" : ""
        }`}
      >
        {value}
      </p>
    </div>
  );
}

export function CommunicationEmailProvenanceCard({
  metadata,
  title = "Email Provenance",
  compact = false,
}: CommunicationEmailProvenanceCardProps) {
  const provenance = parseCommunicationEmailProvenance(metadata);
  if (!provenance.isEmailSync) return null;

  return (
    <Card>
      <CardContent className={compact ? "pt-4" : "pt-6"}>
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-gray-500" />
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
          </div>
          {provenance.sourceLabel ? (
            <Badge
              variant="outline"
              className={`shrink-0 border ${getCommunicationEmailSourceBadgeClass(provenance.source)}`}
            >
              {provenance.sourceLabel}
            </Badge>
          ) : (
            <Badge variant="outline" className="shrink-0 text-gray-600">
              Source not tagged
            </Badge>
          )}
        </div>

        {provenance.tags.length > 0 ? (
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <Tag className="h-3.5 w-3.5 text-gray-500" />
            {provenance.tags.map((tag) => (
              <Badge key={tag} variant="secondary" className="text-xs font-normal">
                {tag}
              </Badge>
            ))}
          </div>
        ) : null}

        <div
          className={`grid gap-4 text-sm ${
            compact ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2"
          }`}
        >
          {provenance.providerLabel ? (
            <ProvenanceField label="Sync provider" value={provenance.providerLabel} />
          ) : null}
          {provenance.syncedMailbox ? (
            <div>
              <span className="text-gray-600 dark:text-gray-400 flex items-center gap-1">
                <Inbox className="h-3.5 w-3.5" />
                Synced mailbox
              </span>
              <p className="text-gray-900 dark:text-gray-100 font-medium mt-1 break-all">
                {provenance.syncedMailbox}
              </p>
            </div>
          ) : null}
          {provenance.contactSelectorEmail ? (
            <ProvenanceField label="Contact email (selector)" value={provenance.contactSelectorEmail} />
          ) : null}
          {provenance.contactSelectorPhone ? (
            <ProvenanceField label="Contact phone (selector)" value={provenance.contactSelectorPhone} />
          ) : null}
          {provenance.emailId ? (
            <ProvenanceField label="Email ID" value={provenance.emailId} mono />
          ) : null}
          {provenance.threadId && provenance.threadId !== provenance.emailId ? (
            <ProvenanceField label="Thread ID" value={provenance.threadId} mono />
          ) : null}
          {provenance.importedVia ? (
            <ProvenanceField label="Imported via" value={provenance.importedVia} />
          ) : null}
          {provenance.indexed != null ? (
            <ProvenanceField label="Search indexed" value={provenance.indexed ? "Yes" : "No"} />
          ) : null}
        </div>

        {!provenance.sourceLabel && provenance.syncedMailbox ? (
          <p className="mt-4 text-xs text-gray-500 dark:text-gray-400 flex items-start gap-1.5">
            <Link2 className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            This email was synced before source tagging existed, or the contact could not be
            classified automatically.
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}
